import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, title = "Account Required", message = "Please log in to continue with your purchase.", isMandatory = false }) => {
  const { login, register, isLoading, error, clearError } = useAuth();
  const [activeTab, setActiveTab] = useState('login'); // 'login' or 'register'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    postalCode: ''
  });
  const [showVerification, setShowVerification] = useState(false);
  const [verificationData, setVerificationData] = useState({ email: '', token: '' });

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) clearError();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const result = await login({ email: formData.email, password: formData.password });
    
    if (result.success) {
      onClose();
    } else if (result.needsVerification) {
      setVerificationData({ email: result.email, token: result.verificationToken });
      setShowVerification(true);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const result = await register(formData);
    
    if (result.success) {
      setVerificationData({ email: formData.email, token: result.user.verificationToken });
      setShowVerification(true);
      setActiveTab('login'); // Switch to login tab after registration
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    // In a real app, this would call verifyEmail from AuthContext
    alert(`Verification token sent to ${verificationData.email}. Check your email and use token: ${verificationData.token}`);
    setShowVerification(false);
    setActiveTab('login');
  };

  const handleClose = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      address: '',
      city: '',
      postalCode: ''
    });
    setShowVerification(false);
    setActiveTab('login');
    clearError();
    onClose();
  };

  // Email Verification View
  if (showVerification) {
    return (
      <div className="login-modal-overlay">
        <div className="login-modal-content">
          <div className="login-modal-header">
            <h2>Email Verification</h2>
            <button className="login-modal-close" onClick={handleClose}>×</button>
          </div>
          
          <div className="login-modal-body">
            <div className="verification-icon">📧</div>
            <h3>Check Your Email</h3>
            <p>We've sent a verification email to <strong>{verificationData.email}</strong></p>
            
            <div className="verification-token-display">
              <p>Verification Token (for testing):</p>
              <code className="verification-token">{verificationData.token}</code>
              <p className="token-note">In production, check your email for the verification link</p>
            </div>
            
            <form onSubmit={handleVerification}>
              <button type="submit" className="login-submit-btn" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'I\'ve Verified My Email'}
              </button>
            </form>
            
            <p className="verification-help">
              Didn't receive the email? Check your spam folder or contact support.
            </p>
          </div>
        </div>
        <div className="login-modal-overlay-backdrop" onClick={handleClose}></div>
      </div>
    );
  }

  // Main Login/Register View
  return (
    <div className="login-modal-overlay">
      <div className="login-modal-content">
        <div className="login-modal-header">
          <h2>{title}</h2>
          {!isMandatory && (
            <button className="login-modal-close" onClick={handleClose}>
              ×
            </button>
          )}
        </div>
        
        <div className="login-modal-body">
          <div className="login-icon">🔐</div>
          <p>{message}</p>

          {/* Tab Navigation */}
          <div className="auth-tabs">
            <button 
              className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
              onClick={() => setActiveTab('login')}
            >
              Login
            </button>
            <button 
              className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
              onClick={() => setActiveTab('register')}
            >
              Register
            </button>
          </div>

          {/* Login Form */}
          {activeTab === 'login' && (
            <form onSubmit={handleLogin} className="auth-form">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email"
                />
              </div>
              
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your password"
                />
              </div>

              {error && <div className="error-message">{error}</div>}
              
              <button type="submit" className="login-submit-btn" disabled={isLoading}>
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}

          {/* Registration Form */}
          {activeTab === 'register' && (
            <form onSubmit={handleRegister} className="auth-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    placeholder="First name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email"
                />
              </div>
              
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Create a password"
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Your phone number"
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Your address"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Your city"
                  />
                </div>
                
                <div className="form-group">
                  <label>Postal Code</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder="Postal code"
                  />
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}
              
              <button type="submit" className="login-submit-btn" disabled={isLoading}>
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}

          <p className="login-benefits">
            By creating an account, your cart and wishlist will be saved and available when you return.
          </p>
          
          {!isMandatory && (
            <button className="continue-guest-btn" onClick={handleClose}>
              Continue as Guest
            </button>
          )}
          
          <div className="login-features">
            <div className="feature">
              <span className="feature-icon">🛒</span>
              <span>Save your cart</span>
            </div>
            <div className="feature">
              <span className="feature-icon">❤️</span>
              <span>Keep your wishlist</span>
            </div>
            <div className="feature">
              <span className="feature-icon">📋</span>
              <span>Order history</span>
            </div>
            <div className="feature">
              <span className="feature-icon">🔄</span>
              <span>Quick reordering</span>
            </div>
          </div>
        </div>
      </div>
      
      {!isMandatory && (
        <div className="login-modal-overlay-backdrop" onClick={handleClose}></div>
      )}
    </div>
  );
};

export default LoginModal;
